import { useMemo, useState } from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { formatMoney, toCents, type Locale } from '@monrdv/shared';
import { request } from '../lib/api.js';
import { RecordLink, PeekButton } from '../components/RecordLink.js';
import { ListToolbar, ListWindowNotice, type FilterChip } from '../components/ListToolbar.js';
import { SortHeader } from '../components/SortHeader.js';
import { Pager } from '../components/Pager.js';
import { downloadExport, useListView } from '../lib/listView.js';

/**
 * Quotes, defaulting to those still awaiting an answer.
 *
 * 1180 of the 2736 quotes in the imported history were accepted and 925 declined, so the
 * useful question is which ones are still open — not a list of settled history. The period
 * narrows that further to the ones somebody could still plausibly chase.
 *
 * The value shown is the open pipeline over **everything the filters match**, computed by the
 * API. It used to be the sum of whatever happened to be on the page, which changed as you paged
 * and was therefore worse than showing no total at all.
 */

type QuoteRow = {
  id: string;
  number: string | null;
  status: string;
  total: string;
  issuedOn: string | null;
  expiresOn: string | null;
  acceptedAt: string | null;
  customer: { id: string | null; name: string | null };
  job: { id: string; number: string } | null;
};

type QuotesResponse = {
  items: QuoteRow[];
  total: number;
  page?: number;
  pageSize?: number;
  pageCount?: number;
  sort?: string;
  dir?: 'asc' | 'desc';
  outsideWindow: number;
  openValue: string;
  acceptedValue: string;
};

const STATUSES = ['draft', 'pending', 'accepted', 'declined', 'expired'] as const;

export function QuotesScreen() {
  const { t: translate, i18n } = useTranslation();
  const t = translate as (key: string, vars?: Record<string, string | number>) => string;
  const locale: Locale = i18n.language === 'en' ? 'en' : 'fr';

  const search = useSearch({ from: '/quotes' });
  const navigate = useNavigate();
  const [exportNote, setExportNote] = useState<{ text: string; failed: boolean } | null>(null);
  const [exporting, setExporting] = useState(false);

  const view = useListView({
    routeId: '/quotes',
    defaultSort: 'issued',
    timeBased: true,
    defaultDateField: 'issued',
    extra: useMemo(() => ({ status: search.status }), [search.status]),
  });

  const quotes = useQuery({
    queryKey: ['quotes', view.params.toString()],
    queryFn: () => request<QuotesResponse>(`/quotes?${view.params}`),
    placeholderData: (prev) => prev,
  });

  const data = quotes.data;
  const rows = data?.items ?? [];

  const chips: FilterChip[] =
    search.status !== 'all'
      ? [
          {
            id: 'status',
            label: `${t('common.status')} : ${t(`quote.status.${search.status}`)}`,
            onRemove: () =>
              navigate({ to: '.', search: (p: any) => ({ ...p, status: 'all', page: undefined }), replace: true }),
          },
        ]
      : [];

  const runExport = async () => {
    setExporting(true);
    setExportNote(null);
    try {
      const outcome = await downloadExport('/quotes/export', view.exportParams, locale);
      setExportNote({
        text: outcome.truncated
          ? t('list.exportTruncated', { limit: outcome.rows, total: outcome.matched })
          : t('list.exportDone', { count: outcome.rows }),
        failed: outcome.truncated,
      });
    } catch {
      setExportNote({ text: t('list.exportFailed'), failed: true });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="screen">
      <header className="screen__head">
        <h1 className="screen__title">{t('quote.many')}</h1>
        <span className="money invoices__figure">
          {t('quote.openValue', { amount: formatMoney(toCents(data?.openValue ?? '0'), locale) })}
        </span>
      </header>

      <ListToolbar
        view={view}
        searchPlaceholder={t('quote.searchPlaceholder')}
        chips={chips}
        onClearAll={() => navigate({ to: '.', search: (p: any) => ({ ...p, status: 'all', page: undefined }), replace: true })}
        dateFields={[
          { value: 'issued', label: t('list.field.issued') },
          { value: 'expires', label: t('list.field.expires') },
          { value: 'created', label: t('list.field.created') },
        ]}
        total={data?.total}
        onExport={() => void runExport()}
        exporting={exporting}
        exportNote={exportNote?.text ?? null}
        exportFailed={exportNote?.failed}
      >
        <label className="listbar__field">
          {t('common.status')}
          <select
            value={search.status}
            onChange={(e) =>
              navigate({ to: '.', search: (p: any) => ({ ...p, status: e.target.value, page: undefined, cursor: undefined }), replace: true })
            }
          >
            <option value="all">{t('common.all')}</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(`quote.status.${value}`)}
              </option>
            ))}
          </select>
        </label>
      </ListToolbar>

      <ListWindowNotice view={view} outsideWindow={data?.outsideWindow ?? 0} empty={rows.length === 0 && !quotes.isPending} />

      {quotes.isError && (
        <div className="notice notice--error" role="alert">
          <p>{t('errors.generic')}</p>
          <button type="button" onClick={() => void quotes.refetch()}>
            {t('common.retry')}
          </button>
        </div>
      )}

      {(quotes.isPending || rows.length > 0) && (
        <table className="table table--dense" aria-label={t('quote.many')}>
          <thead>
            <tr>
              <SortHeader field="number" label={t('list.column.number')} sort={data?.sort} dir={data?.dir} onSort={view.setSort} />
              <SortHeader field="customer" label={t('list.column.customer')} sort={data?.sort} dir={data?.dir} onSort={view.setSort} />
              <th scope="col">
                <span className="label">{t('common.status')}</span>
              </th>
              <SortHeader field="issued" label={t('list.column.issuedOn')} sort={data?.sort} dir={data?.dir} onSort={view.setSort} />
              <SortHeader field="expires" label={t('list.column.expiresOn')} sort={data?.sort} dir={data?.dir} onSort={view.setSort} />
              <SortHeader field="total" label={t('list.column.total')} sort={data?.sort} dir={data?.dir} onSort={view.setSort} numeric />
              <th scope="col" aria-label={t('list.column.actions')} />
            </tr>
          </thead>
          <tbody>
            {quotes.isPending && (
              <tr>
                <td colSpan={7} className="cell cell--loading">
                  {t('common.loading')}
                </td>
              </tr>
            )}
            {rows.map((quote) => (
              <tr key={quote.id}>
                <td className="cell">
                  <RecordLink kind="quote" id={quote.id} className="cell__link">
                    <span className="tnum">{quote.number || t('quote.noNumber')}</span>
                  </RecordLink>
                </td>
                <td className="cell cell--name">
                  {quote.customer?.id ? (
                    <RecordLink kind="customer" id={quote.customer.id} className="cell__link">
                      {quote.customer.name ?? '—'}
                    </RecordLink>
                  ) : (
                    <span className="cell__none">—</span>
                  )}
                </td>
                <td className={`cell quote__status quote__status--${quote.status}`}>{t(`quote.status.${quote.status}`)}</td>
                <td className="cell cell--activity">{day(quote.issuedOn, locale)}</td>
                <td className="cell cell--activity">{day(quote.expiresOn, locale)}</td>
                <td className="cell numeric">
                  <span className="money">{formatMoney(toCents(quote.total), locale)}</span>
                </td>
                <td className="cell" style={{ width: 32 }}>
                  <PeekButton kind="quote" id={quote.id} label={t('quote.preview')} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!quotes.isPending && rows.length === 0 && (
        <div className="empty">
          <h2 className="empty__title">{t('common.noResults')}</h2>
          <p className="empty__body">{t('quote.emptyFiltered')}</p>
        </div>
      )}

      {data && (
        <Pager
          page={data.page ?? view.page}
          pageCount={data.pageCount ?? 1}
          pageSize={data.pageSize ?? view.pageSize}
          total={data.total}
          count={rows.length}
          label={t('quote.many').toLowerCase()}
          busy={quotes.isFetching}
          onPage={view.setPage}
          onPageSize={view.setPageSize}
        />
      )}
    </div>
  );
}

function day(iso: string | null, locale: Locale): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(locale === 'fr' ? 'fr-CA' : 'en-CA');
}
